import { Brain, Heart, Activity, Users, Eye, Zap, Info } from "lucide-react";

interface Phenomenon {
  id: string;
  display_name: string;
  category: string;
  subdomain_weights: Record<string, number>;
  false_v_contribution?: { smc: number; msf: number; ifr: number; k: number };
  linked_evidence_ids: string[];
}

interface Props {
  phenomena: Phenomenon[];
  onPhenomenonHover?: (evidenceIds: string[] | null) => void;
}

const categoryConfig: Record<string, { icon: typeof Brain; color: string }> = {
  Emotion: { icon: Heart, color: "text-red-500 bg-red-50" },
  Cognition: { icon: Brain, color: "text-purple-500 bg-purple-50" },
  Body: { icon: Activity, color: "text-green-500 bg-green-50" },
  Behavior: { icon: Zap, color: "text-amber-500 bg-amber-50" },
  Relational: { icon: Users, color: "text-blue-500 bg-blue-50" },
  Environmental: { icon: Eye, color: "text-teal-500 bg-teal-50" },
};

const FALSE_V_INFO: Record<string, { label: string; tip: string }> = {
  smc: { label: "SMC", tip: "Structural Mode Count — information gap between parallel tasks" },
  msf: { label: "MSF", tip: "Mode Switching Frequency — task switching rate" },
  ifr: { label: "SRF", tip: "Self-Representation Friction — internal self-model vs external presentation mismatch" },
  k: { label: "ECL", tip: "Effective Cognitive Load — unresolved processing load (gap from flow / mastery)" },
};

/** Higher contribution = more red (worse) */
function getContribColor(v: number): string {
  if (v <= 0.15) return "text-emerald-500";
  if (v <= 0.3) return "text-green-500";
  if (v <= 0.5) return "text-amber-500";
  if (v <= 0.7) return "text-orange-500";
  return "text-red-500";
}

export default function PhenomenonPanel({ phenomena, onPhenomenonHover }: Props) {
  return (
    <div className="space-y-2 md:space-y-3">
      <h3 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
        <Brain className="h-4 w-4 md:h-5 md:w-5 text-primary" />
        Phenomenon Layer
      </h3>
      <p className="text-xs md:text-sm text-muted-foreground">
        Observable experiences & patterns detected
      </p>
      <div className="space-y-2 md:space-y-3">
        {phenomena.map((ph) => {
          const cfg = categoryConfig[ph.category] || categoryConfig.Cognition;
          const Icon = cfg.icon;
          const topSubdomains = Object.entries(ph.subdomain_weights)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3);

          const fv = ph.false_v_contribution || null;

          return (
            <div
              key={ph.id}
              className="rounded-lg border border-border bg-card p-3 md:p-4 hover:shadow-sm transition-all"
              onMouseEnter={() => onPhenomenonHover?.(ph.linked_evidence_ids)}
              onMouseLeave={() => onPhenomenonHover?.(null)}
            >
              <div className="flex items-center gap-2 mb-2 md:mb-3">
                <span className={`p-1 md:p-1.5 rounded-md ${cfg.color}`}>
                  <Icon className="h-3.5 w-3.5 md:h-4 md:w-4" />
                </span>
                <span className="font-semibold text-foreground text-sm md:text-base">{ph.display_name}</span>
                <span className="text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                  {ph.category}
                </span>
              </div>

              {/* Top subdomain weights */}
              <div className="space-y-1 md:space-y-1.5 mb-2 md:mb-3">
                {topSubdomains.map(([name, weight]) => (
                  <div key={name} className="flex items-center gap-1.5 md:gap-2">
                    <span className="text-[10px] md:text-xs text-muted-foreground w-24 md:w-36 truncate">{name}</span>
                    <div className="flex-1 h-1 md:h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary/70 rounded-full"
                        style={{ width: `${weight * 100}%` }}
                      />
                    </div>
                    <span className="text-[10px] md:text-xs text-muted-foreground w-7 md:w-8 text-right">
                      {Math.round(weight * 100)}%
                    </span>
                  </div>
                ))}
              </div>

              {/* False-V contribution */}
              {fv && (
                <div className="flex gap-2 md:gap-3">
                  {(["smc", "msf", "ifr", "k"] as const).map((key) => {
                    const val = fv[key] ?? 0;
                    const info = FALSE_V_INFO[key];
                    return (
                      <div key={key} className="text-center group relative">
                        <div className="flex items-center justify-center gap-0.5">
                          <span className="text-[9px] md:text-[10px] font-bold text-muted-foreground uppercase">{info.label}</span>
                          <Info className="h-2 w-2 md:h-2.5 md:w-2.5 text-muted-foreground/50 cursor-help" />
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-foreground text-background text-[10px] rounded-md max-w-[200px] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-normal">
                            {info.tip}
                          </div>
                        </div>
                        <div className={`text-[10px] md:text-xs font-semibold ${getContribColor(val)}`}>
                          {Math.round(val * 100)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
